import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import {
  onboardingApi,
  type AccountType,
  type PlanPreview,
  type PolicyJson,
  type PolicyVersionMeta,
} from '../../api/onboarding.api.js';
import { settingsHistoryKey, type SettingsHistoryEntry } from '../../api/settings-history.api.js';
import { policyRestoreApi } from '../../api/settings-restore.api.js';
import { APP_LOCALE } from '../../utils/locale.js';
import { SettingsHistoryButton, useSettingsLastChanged } from '../settings/SettingsHistory.js';
import { RestoreReasonDialog } from '../settings/SettingsRestore.js';
import { SettingsSectionHeader } from '../settings/SettingsSectionHeader.js';
import { useCanWriteSection, useSectionRestore } from '../settings/useSettingsAccess.js';

type PolicyTab = 'overview' | 'editor' | 'versions' | 'preview';

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function asArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map((item) => asRecord(item)) : [];
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function conditionSummary(condition: unknown): string {
  if (condition === undefined || condition === null) return 'always';
  return JSON.stringify(condition);
}

const VERSION_STATUS_TONE: Record<string, 'success' | 'info' | 'default' | 'warning'> = {
  ACTIVE: 'success',
  DRAFT: 'info',
  ARCHIVED: 'default',
  BUILT_IN: 'warning',
};

function VersionStatusChip({ status }: { status: string }): JSX.Element {
  const color = VERSION_STATUS_TONE[status] ?? 'default';
  return <Chip label={status} size="small" color={color} />;
}

function OverviewTab({ policy, active }: { policy: PolicyJson; active: PolicyVersionMeta }): JSX.Element {
  const jurisdictions = asArray(policy['jurisdictions']);
  const stages = asArray(policy['stages']);

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle1">
            Active policy <b>{asString(policy['version'])}</b>
          </Typography>
          <VersionStatusChip status={active.status} />
          <Typography variant="body2" color="text.secondary">
            Default jurisdiction: <b>{asString(policy['defaultJurisdiction'])}</b>
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {active.changeSummary}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom>
        Jurisdictions
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        {jurisdictions.map((jurisdiction) => {
          const consents = asArray(jurisdiction['consents']);
          const blocked = asArray(jurisdiction['blockedSubregions']);
          const documents = Array.isArray(jurisdiction['identityDocuments'])
            ? (jurisdiction['identityDocuments'] as unknown[]).map((d) => asString(d))
            : [];
          return (
            <Paper key={asString(jurisdiction['id'])} sx={{ p: 2, flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1">{asString(jurisdiction['displayName'])}</Typography>
                <Chip label={asString(jurisdiction['id'])} size="small" />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {asString(jurisdiction['currency'])} ·{' '}
                {asString(jurisdiction['accountIdentifier'])} · AML:{' '}
                {asString(asRecord(jurisdiction['amlRegulation'])['actName'])} · Privacy:{' '}
                {asString(asRecord(jurisdiction['privacyRegulation'])['actName'])}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                ID documents: {documents.join(', ') || '—'}
              </Typography>
              <Typography variant="body2">
                Consents: {String(consents.length)} · Blocked subregions:{' '}
                {blocked.length === 0 ? 'none' : blocked.map((b) => asString(b['code'])).join(', ')}
              </Typography>
            </Paper>
          );
        })}
      </Stack>

      <Typography variant="h6" gutterBottom>
        Flow stages ({String(stages.length)})
      </Typography>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Stage</TableCell>
              <TableCell>Phase</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Applies when</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stages.map((stage) => (
              <TableRow key={asString(stage['id'])}>
                <TableCell>
                  <code>{asString(stage['id'])}</code>
                </TableCell>
                <TableCell>
                  <Chip label={asString(stage['phase'])} size="small" />
                </TableCell>
                <TableCell>{asString(stage['label'])}</TableCell>
                <TableCell>
                  <Typography variant="caption" component="code" sx={{ wordBreak: 'break-all' }}>
                    {conditionSummary(stage['appliesWhen'])}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </>
  );
}

export function OnboardingPolicyPage(): JSX.Element {
  const queryClient = useQueryClient();
  // DEV-140: Admin and AML / MLRO change the policy (DEV-67 row 18).
  const isAdmin = useCanWriteSection({ group: 'compliance', id: 'onboarding-policy' });
  const lastChanged = useSettingsLastChanged('ONBOARDING_POLICY');

  const [tab, setTab] = useState<PolicyTab>('overview');
  const [editorText, setEditorText] = useState('');
  const [editorDirty, setEditorDirty] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [editorError, setEditorError] = useState<string | null>(null);
  const [editorNotice, setEditorNotice] = useState<string | null>(null);

  const [previewType, setPreviewType] = useState<AccountType>('PERSONAL');
  const [previewCountry, setPreviewCountry] = useState('');
  const [previewSource, setPreviewSource] = useState<'active' | 'editor'>('active');
  const [preview, setPreview] = useState<PlanPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const { data: overview, isLoading, error: loadError } = useQuery({
    queryKey: ['onboarding-policy'],
    queryFn: async () => onboardingApi.getPolicy(),
  });

  // Seed the editor with the active policy once loaded (unless the admin
  // already started editing).
  useEffect(() => {
    if (overview !== undefined && !editorDirty) {
      setEditorText(JSON.stringify(overview.policy, null, 2));
    }
  }, [overview, editorDirty]);

  const saveMutation = useMutation({
    mutationFn: async (input: { policy: PolicyJson; changeSummary: string; activate: boolean }) =>
      onboardingApi.createPolicyVersion(input),
    onSuccess: async (meta, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['onboarding-policy'] });
      void queryClient.invalidateQueries({ queryKey: settingsHistoryKey('ONBOARDING_POLICY') });
      // After "Save & activate" the refetched active policy equals the editor
      // content, so resyncing is safe. After a draft save, keep the editor
      // dirty so the seed effect does not replace the draft with the
      // (unchanged) active policy.
      if (variables.activate) setEditorDirty(false);
      setChangeSummary('');
      setEditorError(null);
      setEditorNotice(
        meta.status === 'ACTIVE'
          ? `Version ${meta.version} saved and activated.`
          : `Version ${meta.version} saved as draft.`,
      );
    },
    onError: (err) => {
      setEditorNotice(null);
      setEditorError(err instanceof Error ? err.message : 'Failed to save policy');
    },
  });

  const loadVersionIntoEditor = async (versionId: string): Promise<void> => {
    try {
      const detail = await onboardingApi.getPolicyVersion(versionId);
      setEditorText(JSON.stringify(detail.policy, null, 2));
      setEditorDirty(true);
      setEditorError(null);
      setEditorNotice(`Loaded version ${detail.meta.version} into the editor (unsaved).`);
      setTab('editor');
    } catch (err) {
      setEditorError(err instanceof Error ? err.message : 'Failed to load version');
      setTab('editor');
    }
  };

  const activateMutation = useMutation({
    mutationFn: async (id: string) => onboardingApi.activatePolicyVersion(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['onboarding-policy'] });
      void queryClient.invalidateQueries({ queryKey: settingsHistoryKey('ONBOARDING_POLICY') });
    },
  });

  // DEV-142: restore an archived version (active again, with a reason).
  const restore = useSectionRestore({ group: 'compliance', id: 'onboarding-policy' });
  const [restoreId, setRestoreId] = useState<string | null>(null);
  const [restoreNotice, setRestoreNotice] = useState<string | null>(null);
  const restoreMeta = overview?.versions.find((v) => v.id === restoreId);
  const restoreMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => policyRestoreApi.restore(id, reason),
    onSuccess: async () => {
      setRestoreNotice(`Version ${restoreMeta?.version ?? ''} restored: it is the active policy again.`);
      setRestoreId(null);
      await queryClient.invalidateQueries({ queryKey: ['onboarding-policy'] });
      void queryClient.invalidateQueries({ queryKey: settingsHistoryKey('ONBOARDING_POLICY') });
    },
  });

  const parseEditor = (): PolicyJson | null => {
    try {
      const parsed: unknown = JSON.parse(editorText);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setEditorError('Policy must be a JSON object');
        return null;
      }
      setEditorError(null);
      return parsed as PolicyJson;
    } catch (err) {
      setEditorError(`Invalid JSON: ${err instanceof Error ? err.message : 'parse error'}`);
      return null;
    }
  };

  const save = (activate: boolean): void => {
    const policy = parseEditor();
    if (policy === null) return;
    if (changeSummary.trim().length < 4) {
      setEditorError('Change summary is required (min 4 characters)');
      return;
    }
    saveMutation.mutate({ policy, changeSummary: changeSummary.trim(), activate });
  };

  const runPreview = async (): Promise<void> => {
    setPreviewError(null);
    setPreview(null);
    try {
      const input: Parameters<typeof onboardingApi.previewPlan>[0] = {
        accountType: previewType,
        ...(previewCountry.trim().length === 2 ? { country: previewCountry.trim() } : {}),
      };
      if (previewSource === 'editor') {
        const policy = parseEditor();
        if (policy === null) {
          setPreviewError('Fix the editor JSON first');
          return;
        }
        input.policy = policy;
      }
      setPreview(await onboardingApi.previewPlan(input));
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Preview failed');
    }
  };

  if (isLoading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress />
      </Stack>
    );
  }

  if (loadError !== null && loadError !== undefined) {
    return (
      <Alert severity="error">
        Failed to load onboarding policy:{' '}
        {loadError instanceof Error ? loadError.message : 'unknown error'}
      </Alert>
    );
  }

  if (overview === undefined) return <></>;

  return (
    <>
      <SettingsSectionHeader
        title="Onboarding policy"
        lastChanged={lastChanged}
        actions={<SettingsHistoryButton subject="ONBOARDING_POLICY" title="Onboarding policy"
          {...(restore.canRestore ? { onRestore: (entry: SettingsHistoryEntry) => setRestoreId(entry.restoreTarget ?? null) } : {})} />}
      >
        The criteria-driven flow served to the customer web app. Changes apply to new sessions as
        soon as a version is activated — the web client fetches the policy at startup.
      </SettingsSectionHeader>

      {restoreNotice !== null ? <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRestoreNotice(null)}>{restoreNotice}</Alert> : null}
      <RestoreReasonDialog
        open={restoreId !== null}
        title={`Restore version ${restoreMeta?.version ?? ''}`}
        description={`Version ${restoreMeta?.version ?? ''} becomes the active policy again, in place of version ${overview.active.version}. New onboarding sessions use it from now on; the restore is kept in the History.`}
        saving={restoreMutation.isPending}
        {...(restoreMutation.isError ? { error: restoreMutation.error instanceof Error ? restoreMutation.error.message : 'The version could not be restored.' } : {})}
        onClose={() => { setRestoreId(null); restoreMutation.reset(); }}
        onConfirm={(reason) => { if (restoreId !== null) restoreMutation.mutate({ id: restoreId, reason }); }}
      />

      <Tabs value={tab} onChange={(_e, v: PolicyTab) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Overview" value="overview" />
        <Tab label="Editor" value="editor" />
        <Tab label="Versions" value="versions" />
        <Tab label="Preview" value="preview" />
      </Tabs>

      {tab === 'overview' ? <OverviewTab policy={overview.policy} active={overview.active} /> : null}

      {tab === 'editor' ? (
        <Paper sx={{ p: 2 }}>
          {!isAdmin ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Read-only view. Your role cannot save or activate policy versions.
            </Alert>
          ) : null}
          {editorError !== null ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editorError}
            </Alert>
          ) : null}
          {editorNotice !== null ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {editorNotice}
            </Alert>
          ) : null}
          <TextField
            fullWidth
            multiline
            minRows={20}
            maxRows={32}
            value={editorText}
            onChange={(e) => {
              setEditorText(e.target.value);
              setEditorDirty(true);
              setEditorNotice(null);
            }}
            slotProps={{
              htmlInput: {
                style: { fontFamily: 'monospace', fontSize: 13 },
                spellCheck: false,
                'aria-label': 'Policy JSON',
              },
            }}
            sx={{ mb: 2 }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Change summary (required)"
              value={changeSummary}
              onChange={(e) => setChangeSummary(e.target.value)}
              sx={{ flex: 1 }}
            />
            <Button variant="outlined" onClick={() => parseEditor()}>
              Validate JSON
            </Button>
            <Button
              variant="outlined"
              disabled={!isAdmin || saveMutation.isPending}
              onClick={() => save(false)}
            >
              Save draft
            </Button>
            <Button
              variant="contained"
              disabled={!isAdmin || saveMutation.isPending}
              onClick={() => save(true)}
            >
              Save &amp; activate
            </Button>
          </Stack>
        </Paper>
      ) : null}

      {tab === 'versions' ? (
        <Paper>
          {activateMutation.isError ? (
            <Alert severity="error">
              Activation failed:{' '}
              {activateMutation.error instanceof Error
                ? activateMutation.error.message
                : 'unknown error'}
            </Alert>
          ) : null}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Change summary</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>By</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {overview.versions.map((version) => (
                <TableRow key={version.id ?? 'built-in'}>
                  <TableCell>
                    <code>{version.version}</code>
                  </TableCell>
                  <TableCell>
                    <VersionStatusChip status={version.status} />
                  </TableCell>
                  <TableCell>{version.changeSummary}</TableCell>
                  <TableCell>
                    {version.id === null ? '—' : new Date(version.createdAt).toLocaleString(APP_LOCALE)}
                  </TableCell>
                  <TableCell>{version.createdBy}</TableCell>
                  <TableCell align="right">
                    {version.id !== null ? (
                      <Button
                        size="small"
                        onClick={() => {
                          if (version.id !== null) void loadVersionIntoEditor(version.id);
                        }}
                      >
                        Load into editor
                      </Button>
                    ) : null}
                    {version.id !== null && version.status === 'DRAFT' ? (
                      <Button
                        size="small"
                        disabled={!isAdmin || activateMutation.isPending}
                        onClick={() => {
                          if (version.id !== null) activateMutation.mutate(version.id);
                        }}
                      >
                        Activate
                      </Button>
                    ) : null}
                    {/* DEV-142: a version that was active before is restored, with a reason. */}
                    {version.id !== null && version.status === 'ARCHIVED' && restore.canRestore ? (
                      <Button size="small" color="warning" onClick={() => setRestoreId(version.id)}>
                        Restore
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : null}

      {tab === 'preview' ? (
        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              select
              label="Account type"
              value={previewType}
              onChange={(e) => setPreviewType(e.target.value as AccountType)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="PERSONAL">PERSONAL</MenuItem>
              <MenuItem value="BUSINESS">BUSINESS</MenuItem>
            </TextField>
            <TextField
              label="Country (ISO-2, optional)"
              value={previewCountry}
              onChange={(e) => setPreviewCountry(e.target.value.toUpperCase())}
              sx={{ minWidth: 180 }}
            />
            <TextField
              select
              label="Policy source"
              value={previewSource}
              onChange={(e) => setPreviewSource(e.target.value as 'active' | 'editor')}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="active">Active policy</MenuItem>
              <MenuItem value="editor">Editor draft (unsaved)</MenuItem>
            </TextField>
            <Button variant="contained" onClick={() => void runPreview()}>
              Resolve plan
            </Button>
          </Stack>

          {previewError !== null ? <Alert severity="error">{previewError}</Alert> : null}

          {preview !== null ? (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                <Chip label={`Source: ${preview.source}`} size="small" />
                <Chip label={`Policy ${preview.policyVersion}`} size="small" />
                <Chip
                  label={`Jurisdiction: ${preview.plan.jurisdiction.displayName} (${preview.plan.jurisdiction.currency} / ${preview.plan.jurisdiction.accountIdentifier})`}
                  size="small"
                />
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Stage</TableCell>
                    <TableCell>Phase</TableCell>
                    <TableCell>Label</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.plan.stages.map((stage, index) => (
                    <TableRow key={stage.id}>
                      <TableCell>{String(index + 1)}</TableCell>
                      <TableCell>
                        <code>{stage.id}</code>
                      </TableCell>
                      <TableCell>
                        <Chip label={stage.phase} size="small" />
                      </TableCell>
                      <TableCell>{stage.label}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : null}
        </Paper>
      ) : null}
    </>
  );
}
